import React from 'react';

import { useQuery } from '@apollo/client/react';
import CSP_CAMPAIGNS_FOR_ACCOUNT_Q from '../gql/getCampaignsForAccount.gql';

import { Table } from '@repo/ui/Table';
import CampaignsTableFilter from './CampaignsTableFilter';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';

import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';

import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { ButtonLink } from '@repo/ui/Button';
import { Link } from 'react-router';

const CAMPAIGNS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  tcr_campaign_id: z
    .string()
    .max(7, { message: 'A campaign ID cannot be more than 7 characters' })
    .default(''),

  usecase: z.string().default(''),
  active: z.enum(['Active', 'Inactive', 'All']).default('All'),
  tcr_brand_id: z.string().optional(),
});

const PAGE_SIZE = 10;

export interface CampaignsTableProps {
  accountId?: string;
  brandId?: string;
}

const CampaignsTable = ({ accountId, brandId }: CampaignsTableProps) => {
  const user = useUser();
  const canViewCampaign = POLICIES['csp_campaigns:show'](user);

  const filter = useZodQuery({
    schema: CAMPAIGNS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, active, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
        active: active === 'Active' ? 'true' : active === 'Inactive' ? 'false' : null,
        tcr_brand_id: brandId,
      },
      coerceValue,
      ['tcr_campaign_id', 'usecase', 'tcr_brand_id']
    ),
  ];

  const { loading, error, data } = useQuery(CSP_CAMPAIGNS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { edges: cspCampaignNodes, pageInfo } = data?.cspCampaignsForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <CampaignsTableFilter />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={cspCampaignNodes?.map((cspCampaign) => cspCampaign.node) ?? []}
        dataType="campaigns"
        columns={[
          {
            title: 'Status',
            width: 'sm',
            Cell({ entry: { active } }) {
              return (
                <div className="">
                  <div className="flex relative gap-2 items-center">
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${active ? 'bg-green-500' : 'bg-red-500'}`}
                    ></div>
                    <span>{active ? 'Active ' : 'Inactive'}</span>
                  </div>
                </div>
              );
            },
          },
          {
            title: 'Campaign ID',

            Cell({ entry: { id, tcr_campaign_id } }) {
              return POLICIES['csp_campaigns:show'](user) ? (
                <Link className="underline" to={`/a/${accountId}/tcr/campaigns/${id}`}>
                  {tcr_campaign_id}
                </Link>
              ) : (
                <p>{tcr_campaign_id}</p>
              );
            },
          },
          {
            title: 'Business Name or DBA',
            Cell({ entry: { brand } }) {
              return <p>{brand?.displayName}</p>;
            },
          },
          {
            title: 'Usecase',
            width: 'lg',
            Cell({ entry: { usecase } }) {
              return <p>{usecase}</p>;
            },
          },

          {
            title: canViewCampaign ? 'Details' : '',
            width: canViewCampaign ? 'sm' : 'hidden',
            Cell({ entry: { id } }) {
              return canViewCampaign ? (
                <ButtonLink as={Link} variant="secondary" href={`/a/${accountId}/tcr/campaigns/${id}`}>
                  View
                </ButtonLink>
              ) : (
                <></>
              );
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default CampaignsTable;
